import { Sun, Wind, Globe, ChevronRight, Award, Users, Zap, ArrowRight, Lock, MapPin, Calendar, Activity } from "lucide-react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";
import { useState } from "react";
import { Link } from "react-router-dom"; // Add this import for navigation

const LandingPage: React.FC = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  return (
    <section className="relative w-full h-50 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Video background with overlay */}
      <video
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
        autoPlay
        muted
        loop
        playsInline
      >
        <source src="/Sense.mp4" type="video/mp4" />
      </video>
      
      {/* Dynamic gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-blue-900/30 to-black/70 z-0" />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Authorized Access Notice - Eye-catching position */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="absolute top-6 left-1/2 transform -translate-x-1/2 z-30"
      >
        <div className="flex items-center gap-2 bg-red-500/20 backdrop-blur-sm border border-red-400/50 text-red-200 px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
          <Lock size={16} className="text-red-400" />
          <span>Authorized Person Access Only</span>
        </div>
      </motion.div>

      {/* Hero Section - Mobile First Responsive */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left Column - Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              className="text-white space-y-6 lg:space-y-8"
            >
              {/* Main Headline - Strong Value Proposition */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4 }}
              >
                <h1 className="text-xl sm:text-2xl lg:text-2xl xl:text-5xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-cyan-400 via-blue-200 to-[#0084CF] bg-clip-text text-transparent">
                    AXONMETRIC
                  </span>
                  <br />
                  <span className="text-white">for the energy leaders</span>
                </h1>
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mt-4 max-w-2xl">
                  AxonMetric is a cutting edge technology company, 
                  <span className="text-cyan-400 font-semibold"> impacting energy domain</span> with advanced analytics and optimization algorithms.
                </p>
              </motion.div>

              {/* Single Primary CTA Button - Updated to go to universal login */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <Link
                  to="/login"  // Changed from "#site-select" to "/login"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-[#0084CF] hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-4 px-8 rounded-xl shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105"
                >
                  <span>Explore</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Column - Bigger Interactive Stats Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="relative lg:max-w-xl ml-auto"
            >
              <div className="backdrop-blur-xl bg-gradient-to-br from-white/15 to-white/5 border border-white/30 shadow-2xl rounded-3xl p-8 space-y-8 mt-16">
                
                {/* Header with Status */}
                <div className="text-center">    
                  <h2 className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cyan-400 to-[#0084CF] bg-clip-text text-transparent mb-3">
                    <CountUp end={10.0} duration={3} decimals={1} />+ GW
                  </h2>
                  <p className="text-lg text-white/90 font-medium">Total Assets Managed</p>
                </div>

                {/* Plant Types - Enhanced Grid */}
                <div className="grid grid-cols-2 gap-6">
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    className="group p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:border-yellow-400/50 cursor-pointer transition-all duration-300"
                  >
                    <Sun className="text-yellow-400 mb-4 group-hover:rotate-12 transition-transform duration-300" size={32} />
                    <div className="text-3xl font-bold text-white mb-2">
                      {/*<CountUp end={11} duration={2.5} />*/}
                    </div>
                    <div className="text-white/80 text-sm font-medium mb-1">Solar Power Plants</div>
                    <div className="text-yellow-400 text-xs">10M+ IOT Sensors</div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    className="group p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:border-blue-400/50 cursor-pointer transition-all duration-300"
                  >
                    <Wind className="text-blue-400 mb-4 group-hover:rotate-12 transition-transform duration-300" size={32} />
                    <div className="text-3xl font-bold text-white mb-2">
                      {/*<CountUp end={2} duration={2.5} /> */}
                    </div>
                    <div className="text-white/80 text-sm font-medium mb-1">Wind Power Plants</div>
                    <div className="text-blue-400 text-xs">2M+ IOT Sensors</div>
                  </motion.div>
                </div>

                {/* Quick Stats - Fixed duplicate styling */}
                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-400 mb-1">24*7</div>
                    <div className="text-xs text-white/70 font-medium">Monitoring</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400 mb-1">99%+</div>
                    <div className="text-xs text-white/70 font-medium">Data Availability</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400 mb-1">95%+</div>
                    <div className="text-xs text-white/70 font-medium">Alerts' Accuracy</div>
                  </div>
                </div>

                {/* Location Info */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <span className="font-medium">Key Deliverables</span>
                  </div>
                  <p className="text-white text-sm mt-2">Advanced Analytics (Solar and Wind), RTC Optimization, Spares' Management, Vendor Management</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Floating Action Buttons for Mobile - Updated to go to universal login */}
      <div className="fixed bottom-6 right-6 z-30 lg:hidden">
        <Link to="/login">  {/* Changed from href="#site-select" to Link with to="/login" */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 2 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex items-center justify-center w-14 h-14 bg-gradient-to-r from-cyan-500 to-[#0084CF] text-white rounded-full shadow-2xl shadow-cyan-500/25"
          >
            <Zap size={24} />
          </motion.div>
        </Link>
      </div>
    </section>
  );
};

export default LandingPage;
